import React from 'react';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { MobileGridSavedViews } from '@glu/grid-mobile-react';
import { fetchUserPreferences, saveUserPreferences } from 'api/workspace/service';
import LocaleContext from 'components/locale/LocaleContext';
import { updateGridView } from 'logic/store/grids/actions';
import MobileGridContext from './logic/MobileGridContext';
import * as actions from './logic/actions';
import getFilteredRows from './logic/getFilteredRows';
import { sortData } from './BasicMobileGrid.utils';

const actionColumns = ['selector', 'possibleActions', 'transferId'];

const BasicMobileGridSavedViews = ({ formattedColumnDefs, gridId, id, savedViews }) => {
  const gridContext = React.useContext(MobileGridContext);
  const locale = React.useContext(LocaleContext);
  const activeGridView = useSelector((state) => state.grids[gridId] || {});
  const reduxDispatch = useDispatch();
  // On mounting of BasicMobileGrid the onSavedViewsChanged is triggered,
  // This loading state is used to decide between loading any existing view changes or switching to a static views
  const [viewIsLoading, setViewIsLoading] = React.useState(true);
  const views = savedViews?.views;

  // Adding user preference views
  React.useEffect(() => {
    fetchUserPreferences(gridId)
      .then((response) => {
        if (response.success) {
          const userSavedViews = response.data.value ? JSON.parse(response.data.value) : [];
          // Test if any views are set to default from preference
          const customDefault = userSavedViews?.filter((view) => view?.viewData?.isDefault) || [];
          const systemViews = views.map((view) => {
            if (customDefault && customDefault.length > 0) {
              return {
                ...view,
                viewData: {
                  ...view.viewData,
                  isDefault:
                    customDefault[0]?.viewData?.id === view?.viewData?.id &&
                    customDefault[0]?.viewData?.isStatic === view?.viewData?.isStatic,
                },
              };
            }
            // If there is no user defined default view then we let the system's default view show like normal
            return view;
          });
          const userDefinedViews = userSavedViews
            .map((view) => {
              // Use primary columns from first static view in the case a custom view does not specify any primary columns
              const defaultPrimaryColumns = views[0]?.primary || [];
              const primary =
                view?.primary && view?.primary.length > 0 ? view?.primary : defaultPrimaryColumns;
              const columns =
                view?.primary && view?.primary.length > 0
                  ? view?.columns
                  : view?.columns?.map((col) => ({
                      ...col,
                      hide: defaultPrimaryColumns.includes(col.colId || col.field)
                        ? false
                        : col?.hide,
                    }));
              return {
                ...view,
                primary,
                columns,
              };
            })
            // Remove duplicate static views from user pref (only used for getting custom default)
            .filter((view) => !view?.viewData?.isStatic);

          const allViews = [
            // Loop through system/static views and remove and add whether it's a default view or not based on user pref
            ...systemViews,
            // Loop through user defined views and remove any static views saved from user pref
            ...userDefinedViews,
          ];

          const activeView =
            activeGridView?.activeViewId ||
            allViews?.filter((view) => view?.viewData?.isDefault)[0]?.viewData?.id;

          actions.addUserView({
            dispatch: gridContext.dispatch,
            payload: {
              // Set active view to the 'default' view (when MobileGridSavedViews mounts it triggers the onSavedViewsChanged function)
              activeView,
              views: allViews,
            },
          });
        }
        setViewIsLoading(false);
      })
      .catch(() => setViewIsLoading(false));
    return () => setViewIsLoading(true);
    // NOTE: activeGridView.activeViewId changes on selecting different view so we're not adding it to dep array
    // NOTE/TODO: views is also causing re-rerunning when added to dep array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridContext.dispatch, gridId]);

  const onSavedViewsChanged = React.useCallback(
    (savedViewsResp) => {
      // Format view properties to convert to mobile grid state to change to
      const activeView = savedViewsResp?.value?.views?.find(
        (view) => view?.viewData?.id === savedViewsResp?.value?.active,
      );
      const primaryColumns = activeView?.primary || [];
      const viewColumnState = activeView?.columns || [];
      const columnState = [
        ...primaryColumns
          .map((params) => {
            // params comes from savedViewResp.value.views which is an array of objects
            const colId = typeof params === 'object' ? params?.colId : params;
            // This gets the hide status of the static view column (Example: a saved view where the user hides one of the primary columns)
            const viewMatch = viewColumnState.find((viewColumn) => viewColumn.colId === colId) || {
              hide: false,
            };
            // This gets the rest of the properties of the primary column
            const columnDefinitionMatch = formattedColumnDefs.find(
              (columnDef) => columnDef.colId === colId,
            );
            // If there is no column definition for this primary column then ignore
            if (!columnDefinitionMatch) return null;
            return { ...columnDefinitionMatch, primary: true, hide: viewMatch.hide };
          })
          .filter(Boolean),
        ...viewColumnState
          // Remove primary columns from this section
          .filter((col) => {
            if (!primaryColumns[0]) return true;
            const array =
              typeof primaryColumns[0] === 'object'
                ? primaryColumns.map((col) => col.colId)
                : primaryColumns;
            return !array.includes(col.colId);
          })
          // Map through static view columns to get correct order
          .map((col) => {
            const { colId, hide } = col;
            // This gets the rest of the properties of the column
            const columnDefinitionMatch = formattedColumnDefs.find(
              (columnDef) => columnDef.colId === colId,
            );
            if (!columnDefinitionMatch) return null;
            return { ...columnDefinitionMatch, primary: undefined, hide };
          }),
      ];

      // If the view is first loaded, has a previously modified redux state stored then load with these changes
      const shouldLoadWithReduxMods =
        viewIsLoading && typeof activeGridView?.activeViewId !== 'undefined';

      // Sorts when coming from views will be an array of objects (web formatting)
      // on mobile sortState must be an object with different properties
      const viewSortState = {};
      if (get(activeView, 'sort[0].sort'))
        viewSortState.direction = get(activeView, 'sort[0].sort');
      if (get(activeView, 'sort[0].colId')) viewSortState.field = get(activeView, 'sort[0].colId');
      const sortState = viewSortState;

      // Filters match between web and mobile
      const filterState = activeView?.filters;

      const reduxColumnState = activeGridView?.columnState?.length
        ? activeGridView.columnState
        : columnState;
      const reduxFilterState = activeGridView?.filterState?.length
        ? activeGridView.filterState
        : activeView?.filters;
      const reduxSortState = activeGridView?.sortState?.direction
        ? activeGridView.sortState
        : sortState;
      const reduxActiveView = activeGridView?.activeViewId || savedViewsResp?.value?.active;

      // When Saved Views Change — update all the grid states and refilter/sort data
      const { paginatedData, totalRows } = getFilteredRows({
        columnState: shouldLoadWithReduxMods ? reduxColumnState : columnState,
        filterState: shouldLoadWithReduxMods ? reduxFilterState : filterState,
        sortState: shouldLoadWithReduxMods ? reduxSortState : sortState,
        formattedColumnDefs,
        page: gridContext.page,
        pageSize: gridContext.pageSize,
        rows: gridContext.totalData,
      });

      const userSavedViews =
        savedViewsResp?.value?.views?.filter((view) => !view?.viewData?.isStatic) || [];

      const customDefault = userSavedViews?.filter((view) => view?.viewData?.isDefault) || [];
      const savedViewState = {
        active: shouldLoadWithReduxMods ? reduxActiveView : savedViewsResp?.value?.active,
        views: savedViewsResp?.value?.views?.map((view) => {
          // Handle custom defaults
          if (customDefault && customDefault.length > 0) {
            return {
              ...view,
              viewData: {
                ...view?.viewData,
                isDefault:
                  customDefault[0]?.viewData?.id === view?.viewData?.id &&
                  customDefault[0]?.viewData?.isStatic === view?.viewData?.isStatic,
              },
            };
          }
          return view;
        }),
      };

      actions.changeSavedView({
        gridContext,
        payload: {
          data: sortData({
            sortState: shouldLoadWithReduxMods ? reduxSortState : sortState,
            columnDefs: formattedColumnDefs,
            rows: paginatedData,
          }),
          columnState: shouldLoadWithReduxMods ? reduxColumnState : columnState,
          columnsWithoutActions: shouldLoadWithReduxMods
            ? reduxColumnState.filter((col) => !actionColumns.includes(col?.colId))
            : columnState.filter((col) => !actionColumns.includes(col?.colId)),
          sortState: shouldLoadWithReduxMods ? reduxSortState : sortState,
          filterState: shouldLoadWithReduxMods ? reduxFilterState : filterState,
          unAppliedFilterState: shouldLoadWithReduxMods ? reduxFilterState : filterState,
          savedViewState,
          totalRows,
        },
      });

      updateGridView({
        gridId,
        activeViewId: activeView?.viewData?.id,
        dispatch: reduxDispatch,
        columnState: shouldLoadWithReduxMods ? reduxColumnState : columnState,
        sortState: shouldLoadWithReduxMods ? reduxSortState : sortState,
        filterState: shouldLoadWithReduxMods ? reduxFilterState : filterState,
      });
    },
    // TODO: fix these deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      gridContext.columnState,
      reduxDispatch,
      gridId,
      locale,
      gridContext.setGridState,
      viewIsLoading,
    ],
  );
  return (
    <>
      {/* If there are saved views and the page is no longer loading */}
      {!viewIsLoading && savedViews && !gridContext.loading ? (
        <MobileGridSavedViews
          onChange={onSavedViewsChanged}
          id={gridId || id}
          isLoading={gridContext.loading}
          data={{
            columns: formattedColumnDefs,
            filters: gridContext.filterState,
            savedViews: gridContext.savedViewState,
            columnState: gridContext.columnState,
            columnsWithoutActions: gridContext.columnsWithoutActions,
            page: gridContext.page || 1,
            pageSize: gridContext.pageSize || 10,
            primary: gridContext.primaryColumns || [],
            sortState: gridContext.sortState,
            fromInitialCompare: true,
          }}
          comparisonConvertor={(comparisonData) => {
            // NOTE: original saved view formats
            if (comparisonData.fromInitialCompare) {
              const activeView = gridContext?.savedViewState?.views?.find(
                (view) => view.viewData?.id === gridContext.savedViewState?.active,
              );
              // Remove action columns
              const columnsWithoutActions = activeView?.columns.filter(
                (col) => !actionColumns.includes(col.colId),
              );
              const primaryColumns = activeView?.primary || [];
              const columns = [
                // Loop through primary columns and return matching state
                ...primaryColumns
                  .map((colId) => {
                    return columnsWithoutActions.find(
                      (columnWithoutAction) => columnWithoutAction.colId === colId,
                    );
                  })
                  .filter(Boolean)
                  .map((col) => ({ ...col, primary: true, hide: col.hide })),
                ...columnsWithoutActions
                  .filter((col) => !primaryColumns.includes(col.colId))
                  .map((col) => ({ ...col, primary: undefined })),
              ];
              // NOTE: sortState web and mobile properties are slightly different, but api is storing web version in saved views
              const stateToCompare = {
                columns: columns
                  .map((col) => ({
                    colId: col.colId,
                    primary: col.primary === false ? undefined : col.primary,
                    hide: col.hide,
                  }))
                  // Filter out any columns from saved views missing a colId
                  .filter((col) => col.colId),
                sortState:
                  activeView.sort?.map((field) => ({
                    direction: field.sort,
                    field: field.colId,
                  }))[0] || {},
                filterState: activeView.filters,
              };
              return stateToCompare;
            }
            const modifiedStateToCompare = {
              columns: gridContext.columnState
                .filter((col) => !actionColumns.includes(col?.colId))
                .map((col) => ({
                  colId: col?.colId,
                  // If not true then just set to undefined to match correctly
                  primary: col?.primary === false ? undefined : col?.primary,
                  hide: col?.hide,
                }))
                // Filter out any columns from saved views missing a colId
                .filter((col) => col.colId),
              sortState: gridContext.sortState,
              filterState: gridContext.filterState,
            };
            return modifiedStateToCompare;
          }}
          onSave={() => {
            // NOTES:
            // There is an issue that onSavedViewsChanged will run just before this onSave event fires
            // creating out of date grid states based on the old (unupdated) savedView
            const { previousState } = gridContext;
            const { paginatedData, totalRows } = getFilteredRows({
              columnState: previousState.columnState,
              filterState: previousState.filterState,
              sortState: previousState.sortState,
              formattedColumnDefs,
              page: previousState.page,
              pageSize: previousState.pageSize,
              rows: previousState.totalData,
            });
            actions.changeSavedView({
              gridContext,
              payload: {
                data: sortData({
                  sortState: previousState.sortState,
                  columnDefs: formattedColumnDefs,
                  rows: paginatedData,
                }),
                columnState: previousState.columnState,
                columnsWithoutActions: previousState.columnState.filter(
                  (col) => !actionColumns.includes(col?.colId),
                ),
                sortState: previousState.sortState,
                filterState: previousState.filterState,
                unAppliedFilterState: previousState.filterState,
                totalRows,
              },
            });
          }}
          saveView={({ id, newViews }) => {
            // NOTES:
            // There is an issue that onSavedViewsChanged will run just before this saveView event fires
            // creating out of date grid states based on the old (unupdated) savedView
            const modifiedViews = newViews.map((view) => {
              const { filters, page, pageSize, primary, viewData, sortState, sort } = view;
              const viewColumns = view?.columnState || view.columns;
              const columns = viewColumns;
              return {
                viewData,
                columns,
                filters,
                // NOTE: selected / modified view will save based on grid state while the other views will just reuse their prior attributes
                // applies to sort and primary
                sort: sortState?.direction
                  ? [{ sort: sortState.direction, colId: sortState.field }]
                  : sort,
                pageSize,
                page,
                primary: primary?.map((params) => {
                  if (typeof params === 'object') return params?.colId;
                  return params;
                }),
              };
            });
            return saveUserPreferences(id, modifiedViews)
              .then(() => {
                const customDefault =
                  modifiedViews?.filter((view) => view?.viewData?.isDefault) || [];
                const systemViews = views?.map((view) => {
                  // Loop through static views and set default accordingly to user pref
                  if (customDefault && customDefault.length > 0) {
                    return {
                      ...view,
                      viewData: {
                        ...view?.viewData,
                        isDefault:
                          customDefault[0]?.viewData.id === view?.viewData?.id &&
                          customDefault[0]?.viewData.isStatic === view?.viewData?.isStatic,
                      },
                    };
                  }
                  return view;
                });
                const userDefinedViews = modifiedViews.filter((view) => !view?.viewData?.isStatic);
                actions.addUserView({
                  dispatch: gridContext.dispatch,
                  payload: {
                    views: [...systemViews, ...userDefinedViews],
                  },
                });
              })
              .catch(() => {
                // TODO: handle broken views after failed save
              });
          }}
        />
      ) : null}
    </>
  );
};

BasicMobileGridSavedViews.propTypes = {
  formattedColumnDefs: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  gridId: PropTypes.string.isRequired,
  id: PropTypes.string,
  savedViews: PropTypes.shape({
    views: PropTypes.arrayOf(PropTypes.shape({})),
    active: PropTypes.number,
  }),
};

BasicMobileGridSavedViews.defaultProps = {
  id: 'basicMobileGridId',
  savedViews: null,
};

export default BasicMobileGridSavedViews;
